import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchGallery } from '../api/galleryApi';

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    setErr('');
    try {
      const data = await fetchGallery({ page: p, limit: 30 });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(Number(data?.totalPages || 1));
      setPage(Number(data?.page || p));
    } catch (e) {
      setErr(e?.message || 'Failed to load gallery');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') {
        const i = items.findIndex((x) => x.id === lightbox.id);
        if (i >= 0 && i < items.length - 1) setLightbox(items[i + 1]);
      }
      if (e.key === 'ArrowLeft') {
        const i = items.findIndex((x) => x.id === lightbox.id);
        if (i > 0) setLightbox(items[i - 1]);
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, items]);

  return (
    <div className="container" style={{ padding: '2rem 0 3rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="section-title" style={{ marginBottom: '0.35rem' }}>
          Gallery
        </h1>
        <p className="section-subtitle" style={{ margin: 0 }}>
          Click an image for full screen
        </p>
      </div>

      {err && (
        <div className="card" style={{ borderColor: '#5b1a1a', color: '#ffb3b3', marginBottom: '1rem' }}>
          {err}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#999' }}>Loading…</p>
      ) : (
        <div className="site-gallery-grid">
          {items.map((g) => (
            <button
              key={g.id}
              type="button"
              className="site-gallery-cell"
              onClick={() => setLightbox(g)}
              aria-label="Open image"
            >
              <img src={g.url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && !err && (
        <div className="card" style={{ textAlign: 'center', color: '#999' }}>
          No gallery images yet.
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '2rem', flexWrap: 'wrap' }}>
          <button className="btn" type="button" disabled={page <= 1} onClick={() => load(page - 1)}>
            Previous
          </button>
          <span style={{ alignSelf: 'center', color: '#999' }}>
            Page {page} / {totalPages}
          </span>
          <button className="btn" type="button" disabled={page >= totalPages} onClick={() => load(page + 1)}>
            Next
          </button>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Link to="/" className="btn">
          Back to Home
        </Link>
      </div>

      {lightbox && (
        <div
          className="site-gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setLightbox(null)}
        >
          <button type="button" className="site-gallery-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">
            ×
          </button>
          <div className="site-gallery-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt="" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
